<template>
  <Teleport to="body">
    <div class="modal-backdrop" @click.self="$emit('close')">
      <div class="modal-container missing-account-codes-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">Unmapped Barcodes & Descriptions</h2>
              <p class="modal-subtitle">These items need to be mapped to account codes before export</p>
              <div class="header-actions-row">
                <button @click="openCreateAccountCodeModal" class="btn-add">
                  <font-awesome-icon icon="fa-solid fa-plus" />
                  Добави Код
                </button>
              </div>
            </div>
            <button @click="$emit('close')" class="btn btn-icon">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body">
            <div v-if="loading" class="loading-container">
              <div class="spinner-small"></div>
              <span>Saving...</span>
            </div>

            <div v-else class="items-list">

              <div v-for="(invoiceId, idx) in Object.keys(itemsByInvoice)" :key="invoiceId" class="invoice-group">
                <div class="invoice-header">
                  <div class="invoice-title">
                    <div class="invoice-info">
                      <div class="invoice-main-line">
                        <font-awesome-icon icon="fa-solid fa-file-invoice" class="invoice-icon" />
                        <a
                          href="#"
                          class="invoice-number invoice-number-link"
                          title="Отвори оригинала"
                          @click.prevent.stop="openOriginal(invoiceId)"
                        >{{ itemsByInvoice[invoiceId][0].invoice_number }}</a>
                        <span v-if="itemsByInvoice[invoiceId][0].vendor_name" class="vendor-name">
                          {{ itemsByInvoice[invoiceId][0].vendor_name }}
                        </span>
                      </div>
                      <div v-if="itemsByInvoice[invoiceId][0].invoice_date" class="invoice-date">
                        <font-awesome-icon icon="fa-solid fa-calendar-days" class="invoice-date-icon" />
                        {{ formatInvoiceDate(itemsByInvoice[invoiceId][0].invoice_date) }}
                      </div>
                    </div>
                  </div>
                  <div class="invoice-header-actions">
                    <div class="invoice-mapping">
                      <AccountCodeSelector
                        v-model="invoiceAccountCodes[invoiceId]"
                        :account-codes="accountCodes"
                        @update:modelValue="applyInvoiceMapping(invoiceId)"
                      />
                      <InternalCodeSelector
                        v-model="invoiceInternalCodes[invoiceId]"
                        :account-codes="accountCodes"
                        @update:modelValue="applyInvoiceInternalCode(invoiceId)"
                      />
                    </div>
                  </div>
                </div>
                
                <span class="item-count">{{ itemsByInvoice[invoiceId].length }} items</span>

                <div v-for="(item, index) in itemsByInvoice[invoiceId]" :key="index" class="item-row">
                  <div class="item-info">
                    <div v-if="item.type === 'both'" class="combined-item">
                      <div class="item-type-badge barcode">Barcode</div>
                      <div class="item-value">{{ item.barcode }}</div>
                      <div class="item-type-badge description mt-2">Description</div>
                      <div class="item-value">{{ item.description }}</div>
                    </div>
                    <div v-else-if="item.type === 'barcode'" class="combined-item">
                      <div class="item-type-badge barcode">Barcode</div>
                      <div class="item-value">{{ item.value }}</div>
                      <div v-if="item.description" class="item-type-badge description mt-2">Description</div>
                      <div v-if="item.description" class="item-value">{{ item.description }}</div>
                    </div>
                    <div v-else class="combined-item">
                      <div v-if="item.barcode" class="item-type-badge barcode">Barcode</div>
                      <div v-if="item.barcode" class="item-value">{{ item.barcode }}</div>
                      <div class="item-type-badge description" :class="{ 'mt-2': item.barcode }">Description</div>
                      <div class="item-value">{{ item.description || item.value }}</div>
                    </div>
                  </div>
                  <div class="item-mapping">
                    <AccountCodeSelector
                      v-model="item.selectedAccountCode"
                      :account-codes="accountCodes"
                      :required="true"
                      @selected="(val) => propagateToMatching(item, 'selectedAccountCode', val)"
                    />
                    <InternalCodeSelector
                      v-model="item.selectedInternalCode"
                      :account-codes="accountCodes"
                      :required="true"
                      @selected="(val) => propagateToMatching(item, 'selectedInternalCode', val)"
                    />
                    <button 
                      @click="openCreateAccountCodeModalWithData(item)"
                      class="btn-add-quick"
                      title="Add new account code with this data"
                    >
                      <font-awesome-icon icon="fa-solid fa-plus" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>

          <div class="modal-footer">
            <button @click="$emit('close')" class="btn btn-secondary">
              Cancel
            </button>
            <button @click="saveMappings" class="btn btn-primary" :disabled="loading || !allMapped">
              <font-awesome-icon v-if="loading" icon="fa-solid fa-spinner" spin />
              Save & Export
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Account Code Creation Modal - Separate Teleport with higher z-index -->
  <Teleport to="body">
    <div v-if="showCreateModal" class="account-code-modal-wrapper">
      <AccountCodeModal
        :companyId="companyId"
        :prefilledData="prefilledData"
        @close="closeCreateModal"
        @saved="handleAccountCodeCreated"
      />
    </div>
  </Teleport>

  <!-- Original invoice preview — shown OVER this modal, does not close it -->
  <Teleport to="body">
    <div v-if="previewInvoiceId" class="original-preview-backdrop" @click.self="closePreview">
      <div class="original-preview">
        <div class="original-preview-header">
          <span class="original-preview-title">
            <font-awesome-icon icon="fa-solid fa-file-invoice" />
            {{ previewInvoiceNumber }}
          </span>
          <div class="original-preview-actions">
            <button class="original-preview-btn" @click="zoomOut" :disabled="previewZoom <= 50" title="Zoom Out">
              <font-awesome-icon icon="fa-solid fa-magnifying-glass-minus" />
            </button>
            <span class="zoom-level">{{ previewZoom }}%</span>
            <button class="original-preview-btn" @click="resetZoom" title="Reset Zoom">
              <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
            </button>
            <button class="original-preview-btn" @click="zoomIn" :disabled="previewZoom >= 200" title="Zoom In">
              <font-awesome-icon icon="fa-solid fa-magnifying-glass-plus" />
            </button>
            <a :href="previewUrl" target="_blank" rel="noopener" class="original-preview-btn" title="Отвори в нов таб">
              <font-awesome-icon icon="fa-solid fa-up-right-from-square" />
            </a>
            <button class="original-preview-btn" @click="closePreview" title="Затвори">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>
        </div>
        <div 
          ref="frameContainer"
          class="original-preview-frame-container" 
          :class="{ 'is-zoomed': previewZoom > 100, 'is-dragging': isDragging }"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
        >
          <iframe :src="previewUrl" class="original-preview-frame" :style="{ transform: `scale(${previewZoom / 100})` }" title="Оригинал"></iframe>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, provide, watch, nextTick } from 'vue';
import api from '@/utils/api';
import AccountCodeSelector from '@/components/selectors/AccountCodeSelector.vue';
import InternalCodeSelector from '@/components/selectors/InternalCodeSelector.vue';
import AccountCodeModal from '@/components/companies/AccountCodeModal.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  accountCodes: {
    type: Array,
    required: true
  },
  companyId: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['close', 'saved', 'account-code-created']);

const loading = ref(false);
const error = ref(null);
const activeDropdownId = ref(null);
const invoiceAccountCodes = ref({});
const invoiceInternalCodes = ref({});
const showCreateModal = ref(false);
const prefilledData = ref(null);
const currentEditingItem = ref(null);

// Provide active dropdown management to child components
provide('activeDropdownId', activeDropdownId);
provide('setActiveDropdown', (id) => {
  activeDropdownId.value = id;
});

// Watch for changes in accountCodes prop
watch(() => props.accountCodes, (newCodes) => {
  console.log('MissingAccountCodesModal - Account codes updated, count:', newCodes?.length);
});

// Group items by invoice
const itemsByInvoice = computed(() => {
  console.log('MissingAccountCodesModal - Total items:', props.items.length);
  const grouped = {};
  props.items.forEach(item => {
    const invoiceId = item.invoice_id;
    if (!grouped[invoiceId]) {
      grouped[invoiceId] = [];
    }
    grouped[invoiceId].push(item);
  });
  console.log('MissingAccountCodesModal - Grouped by invoice:', Object.keys(grouped).length, 'invoices');
  Object.keys(grouped).forEach(id => {
    console.log(`  Invoice ${grouped[id][0].invoice_number}: ${grouped[id].length} items`);
  });
  return grouped;
});

const allMapped = computed(() => {
  return props.items.every(item => item.selectedAccountCode && item.selectedAccountCode.trim() !== '');
});

// Preview the original scanned invoice in an in-app modal (over this one), so the
// user never leaves the mapping screen.
const previewInvoiceId = ref(null);
const previewInvoiceNumber = ref('');
const previewUrl = computed(() =>
  previewInvoiceId.value ? `/api/invoices/${previewInvoiceId.value}/original` : ''
);

const previewZoom = ref(100);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const scrollStart = ref({ left: 0, top: 0 });
const frameContainer = ref(null);

const openOriginal = (invoiceId) => {
  if (!invoiceId) return;
  previewInvoiceId.value = invoiceId;
  previewInvoiceNumber.value = itemsByInvoice.value[invoiceId]?.[0]?.invoice_number || '';
  previewZoom.value = 100;
};

const closePreview = () => {
  previewInvoiceId.value = null;
  previewInvoiceNumber.value = '';
  previewZoom.value = 100;
  isDragging.value = false;
};

const zoomIn = () => {
  if (previewZoom.value < 200) {
    previewZoom.value += 10;
  }
};

const zoomOut = () => {
  if (previewZoom.value > 50) {
    previewZoom.value -= 10;
  }
};

const resetZoom = () => {
  previewZoom.value = 100;
};

const handleMouseDown = (e) => {
  if (previewZoom.value <= 100) return;
  isDragging.value = true;
  dragStart.value = { x: e.clientX, y: e.clientY };
  scrollStart.value = {
    left: frameContainer.value.scrollLeft,
    top: frameContainer.value.scrollTop
  };
  e.preventDefault();
};

const handleMouseMove = (e) => {
  if (!isDragging.value) return;
  const deltaX = e.clientX - dragStart.value.x;
  const deltaY = e.clientY - dragStart.value.y;
  frameContainer.value.scrollLeft = scrollStart.value.left - deltaX;
  frameContainer.value.scrollTop = scrollStart.value.top - deltaY;
};

const handleMouseUp = () => {
  isDragging.value = false;
};

const handleMouseLeave = () => {
  isDragging.value = false;
};

// Display the invoice date as DD.MM.YYYY when it's an ISO date; otherwise as-is.
const formatInvoiceDate = (value) => {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(value);
};

const applyInvoiceMapping = (invoiceId) => {
  const codeToApply = invoiceAccountCodes.value[invoiceId];
  if (codeToApply && itemsByInvoice.value[invoiceId]) {
    itemsByInvoice.value[invoiceId].forEach(item => {
      item.selectedAccountCode = codeToApply;
    });
    invoiceAccountCodes.value[invoiceId] = '';
  }
};

const applyInvoiceInternalCode = (invoiceId) => {
  const codeToApply = invoiceInternalCodes.value[invoiceId];
  if (codeToApply && itemsByInvoice.value[invoiceId]) {
    itemsByInvoice.value[invoiceId].forEach(item => {
      item.selectedInternalCode = codeToApply;
    });
    invoiceInternalCodes.value[invoiceId] = '';
  }
};

const getItemDescription = (item) => {
  return (item.description || item.value || '').toLowerCase().trim();
};

const propagateToMatching = (sourceItem, field, value) => {
  const sourceDesc = getItemDescription(sourceItem);
  if (!sourceDesc) return;

  props.items.forEach(item => {
    if (item === sourceItem) return;
    if (getItemDescription(item) === sourceDesc) {
      item[field] = value;
    }
  });
};

const openCreateAccountCodeModal = () => {
  console.log('Opening create account code modal, companyId:', props.companyId);
  prefilledData.value = null;
  showCreateModal.value = true;
  console.log('showCreateModal set to:', showCreateModal.value);
};

const openCreateAccountCodeModalWithData = (item) => {
  console.log('Opening create account code modal with data:', item);
  
  // Prepare pre-filled data based on item type
  const data = {
    barcode_mapping: [],
    description_mapping: [],
    account_name: ''
  };
  
  if (item.type === 'barcode') {
    data.barcode_mapping = [item.value];
    if (item.description) {
      data.description_mapping = [item.description];
      data.account_name = item.description;
    }
  } else if (item.type === 'description') {
    const description = item.description || item.value;
    data.description_mapping = [description];
    data.account_name = description;
    if (item.barcode) {
      data.barcode_mapping = [item.barcode];
    }
  } else if (item.type === 'both') {
    data.barcode_mapping = [item.barcode];
    data.description_mapping = [item.description];
    data.account_name = item.description;
  }
  
  prefilledData.value = data;
  currentEditingItem.value = item;
  showCreateModal.value = true;
  console.log('Opening modal with prefilled data:', prefilledData.value);
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  prefilledData.value = null;
  currentEditingItem.value = null;
};

const handleAccountCodeCreated = async (newAccountCode) => {
  console.log('MissingAccountCodesModal - Account code created:', newAccountCode);
  
  // Emit event first to update parent's account codes list
  emit('account-code-created', newAccountCode);
  
  // Auto-fill the item's selectors after the next tick to avoid reactivity conflicts
  if (currentEditingItem.value && newAccountCode) {
    await nextTick();
    console.log('MissingAccountCodesModal - Auto-filling item selectors');
    currentEditingItem.value.selectedAccountCode = newAccountCode.account_code;
    
    // Auto-select internal code if available
    if (newAccountCode.internal_code) {
      currentEditingItem.value.selectedInternalCode = newAccountCode.internal_code;
    }
  }
  
  showCreateModal.value = false;
  prefilledData.value = null;
  currentEditingItem.value = null;
};

const saveMappings = async () => {
  // Check if all items have account codes selected
  const unmapped = props.items.filter(item => !item.selectedAccountCode || item.selectedAccountCode.trim() === '');
  if (unmapped.length > 0) {
    error.value = `Please select account codes for all ${unmapped.length} item(s)`;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Group items by account code and type
    const mappingsByCode = {};
    
    console.log('Starting to process items for saving:', props.items.length);
    
    props.items.forEach(item => {
      console.log('Processing item:', {
        type: item.type,
        value: item.value,
        barcode: item.barcode,
        description: item.description,
        selectedAccountCode: item.selectedAccountCode,
        selectedInternalCode: item.selectedInternalCode
      });
      
      const code = item.selectedAccountCode;
      if (!mappingsByCode[code]) {
        mappingsByCode[code] = {
          barcodes: [],
          descriptions: [],
          barcodeInternalCodes: {},
          descriptionInternalCodes: {}
        };
      }
      
      const internalCode = item.selectedInternalCode || '';
      
      if (item.type === 'barcode') {
        const barcodeVal = (item.value || '').replace(/,/g, '.');
        console.log('Adding barcode:', barcodeVal, 'to code:', code);
        mappingsByCode[code].barcodes.push(barcodeVal);
        if (internalCode) {
          mappingsByCode[code].barcodeInternalCodes[barcodeVal] = internalCode;
        }
      } else if (item.type === 'description') {
        const desc = (item.description || item.value || '').replace(/,/g, '.');
        console.log('Adding description:', desc, 'to code:', code);
        mappingsByCode[code].descriptions.push(desc);
        if (internalCode) {
          mappingsByCode[code].descriptionInternalCodes[desc] = internalCode;
        }
      } else if (item.type === 'both') {
        const barcodeVal = (item.barcode || '').replace(/,/g, '.');
        const descVal = (item.description || '').replace(/,/g, '.');
        console.log('Adding both - barcode:', barcodeVal, 'description:', descVal, 'to code:', code);
        // Add to both barcode and description mappings
        mappingsByCode[code].barcodes.push(barcodeVal);
        mappingsByCode[code].descriptions.push(descVal);
        if (internalCode) {
          mappingsByCode[code].barcodeInternalCodes[barcodeVal] = internalCode;
          mappingsByCode[code].descriptionInternalCodes[descVal] = internalCode;
        }
      }
    });
    
    console.log('Mappings by code:', mappingsByCode);

    // Find account code IDs and append mappings
    const updatePromises = Object.keys(mappingsByCode).map(accountCode => {
      const accountCodeObj = props.accountCodes.find(ac => ac.account_code === accountCode);
      if (!accountCodeObj) {
        console.error('Account code not found:', accountCode);
        return Promise.resolve();
      }

      const mappings = mappingsByCode[accountCode];
      
      // Load existing mappings for this account code
      return api.get(`/api/account-codes/${accountCodeObj.id}`)
        .then(response => {
          const existingCode = response.data;
          const existingBarcodes = existingCode.barcode_mapping || [];
          const existingDescriptions = existingCode.description_mapping || [];
          const existingBarcodeInternalCodes = existingCode.barcode_internal_code_mapping || {};
          const existingDescriptionInternalCodes = existingCode.description_internal_code_mapping || {};
          
          // Merge and deduplicate
          const newBarcodes = [...new Set([...existingBarcodes, ...mappings.barcodes])];
          const newDescriptions = [...new Set([...existingDescriptions, ...mappings.descriptions])];
          
          // Merge internal code mappings
          const newBarcodeInternalCodes = { ...existingBarcodeInternalCodes, ...mappings.barcodeInternalCodes };
          const newDescriptionInternalCodes = { ...existingDescriptionInternalCodes, ...mappings.descriptionInternalCodes };
          
          // Update the account code
          return api.put(`/api/account-codes/${accountCodeObj.id}`, {
            account_code: existingCode.account_code,
            account_name: existingCode.account_name,
            account_type: existingCode.account_type,
            internal_code: existingCode.internal_code,
            category: existingCode.category,
            barcode_mapping: newBarcodes,
            description_mapping: newDescriptions,
            barcode_internal_code_mapping: newBarcodeInternalCodes,
            description_internal_code_mapping: newDescriptionInternalCodes,
            is_active: existingCode.is_active,
            is_default: existingCode.is_default
          });
        });
    });

    await Promise.all(updatePromises);
    
    emit('saved');
  } catch (err) {
    console.error('Failed to save mappings:', err);
    
    if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else {
      error.value = 'Failed to save mappings. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow-x: auto;
}

.modal-container {
  position: relative;
  z-index: 9999;
  width: 1378px !important;
  min-width: 1378px !important;
  margin: 0 auto;
}

.missing-account-codes-modal {
  max-width: 1378px !important;
  width: 1378px !important;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #ffffff, #fafbfc);
  gap: 16px;
  border-radius: 12px 12px 0 0;
}

.modal-title-section {
  flex: 1;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #111827;
  letter-spacing: -0.01em;
}

.modal-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 6px;
  margin-bottom: 12px;
  font-weight: 400;
}

.header-actions-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: nowrap;
}

.modal-title-section .btn-add {
  margin-top: 8px;
}

.btn-icon {
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #6b7280;
  font-size: 15px;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.invoice-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  margin-bottom: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.invoice-header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.invoice-mapping {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
  align-items: center;
}

.invoice-mapping > .account-code-selector {
  flex: 1;
  min-width: 300px;
}

.invoice-mapping > .internal-code-selector {
  flex: 0 0 auto;
  width: auto;
}
.invoice-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #1e293b;
  font-size: 15px;
}

.invoice-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.invoice-main-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.invoice-icon {
  color: #3b82f6;
  font-size: 18px;
}

.invoice-number {
  color: #0f172a;
  font-weight: 700;
}

.invoice-number-link {
  color: #2563eb;
  text-decoration: none;
  cursor: pointer;
}

.invoice-number-link:hover {
  text-decoration: underline;
}

.invoice-date {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}

.invoice-date-icon {
  font-size: 11px;
  color: #94a3b8;
}

.vendor-name {
  color: #64748b;
  font-weight: 500;
  padding-left: 8px;
  border-left: 2px solid #cbd5e1;
}

.item-count {
  display: block;
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.btn-add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
  max-width: 150px;
  height: 40px;
}

.btn-add svg {
  flex-shrink: 0;
}

.btn-add:hover {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(14, 165, 233, 0.35);
}

.btn-add:active {
  transform: translateY(0);
}

.item-row {
  display: flex;
  align-items: flex-start;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  gap: 12px;
  background: white;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.item-row:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-type-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 0;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  width: fit-content;
}

.item-type-badge.barcode {
  color: #1e40af;
}

.item-type-badge.description {
  color: #6b21a8;
}

.item-type-badge.both {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  color: #92400e;
  border: 1px solid #fbbf24;
}

.combined-item {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 8px;
  row-gap: 8px;
  align-items: center;
}

.combined-item > * {
  flex-shrink: 0;
}

.combined-item .mt-2 {
  margin-top: 0;
  margin-left: 0;
}

.item-value {
  font-weight: 600;
  color: #111827;
  margin-bottom: 0;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.4;
  display: inline-block;
}

.item-description,
.item-barcode {
  font-size: 13px;
  color: #6b7280;
  margin-top: 6px;
  font-weight: 500;
  padding: 6px 10px;
  background: #f9fafb;
  border-radius: 6px;
  border-left: 3px solid #e5e7eb;
}

.item-mapping {
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
  max-width: 540px;
}

.item-mapping > .account-code-selector {
  flex: 1;
  min-width: 280px;
  max-width: 420px;
}

.item-mapping > .internal-code-selector {
  flex: 0 0 auto;
  width: auto;
}

.item-mapping .btn-add-quick {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: #0ea5e9;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.item-mapping .btn-add-quick svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: block;
}

.item-mapping .btn-add-quick:hover {
  background: #0284c7;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.item-mapping .btn-add-quick:active {
  transform: translateY(0);
}

.item-mapping select {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.item-mapping select:hover {
  border-color: #cbd5e1;
}

.item-mapping select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.error-message {
  padding: 14px 18px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 500;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 28px;
  border-top: 1px solid #e5e7eb;
  background: #fafbfc;
  border-radius: 0 0 12px 12px;
}

.btn {
  padding: 11px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.01em;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

/* Account Code Modal Wrapper - Ensure it appears above this modal */
.account-code-modal-wrapper {
  z-index: 10100;
}

.account-code-modal-wrapper :deep(.modal-overlay) {
  z-index: 10100 !important;
}

/* Original invoice preview overlay */
.original-preview-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10200;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.original-preview {
  background: #fff;
  border-radius: 12px;
  width: min(1400px, 95vw);
  height: min(95vh, 1400px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.original-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 2px solid #e2e8f0;
  background: linear-gradient(to bottom, #ffffff, #f8fafc);
  min-height: 80px;
}

.original-preview-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  font-size: 18px;
  color: #2563eb;
}

.original-preview-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.zoom-level {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 55px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  background: #f1f5f9;
  border-radius: 6px;
  user-select: none;
}

.original-preview-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s ease;
  font-size: 14px;
}

.original-preview-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.original-preview-btn:hover:not(:disabled) {
  background: #eef2f7;
  color: #0f172a;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.original-preview-frame-container {
  flex: 1;
  overflow: auto;
  background: #f1f5f9;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  user-select: none;
}

.original-preview-frame-container.is-zoomed {
  cursor: grab;
}

.original-preview-frame-container.is-dragging {
  cursor: grabbing;
}

.original-preview-frame {
  min-width: 100%;
  min-height: 100%;
  border: 0;
  background: #ffffff;
  transform-origin: top left;
  transition: transform 0.2s ease;
  pointer-events: none;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .modal-backdrop {
    padding: 10px;
  }

  .modal-content {
    max-height: 95vh;
  }

  .modal-header {
    padding: 16px;
    flex-wrap: wrap;
  }

  .modal-header-actions {
    width: 100%;
    order: -1;
    justify-content: flex-end;
    margin-bottom: 12px;
  }

  .modal-title-section {
    width: 100%;
  }

  .modal-title {
    font-size: 18px;
  }

  .modal-subtitle {
    font-size: 13px;
  }

  .header-actions-row {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-body {
    padding: 16px;
  }

  .btn-add {
    width: 100%;
    justify-content: center;
  }

  .invoice-header {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 12px;
  }

  .invoice-title {
    flex-wrap: wrap;
    font-size: 14px;
  }

  .invoice-number {
    font-size: 14px;
  }

  .vendor-name {
    font-size: 13px;
    padding-left: 6px;
    flex-basis: 100%;
    border-left: none;
    padding-top: 4px;
  }

  .invoice-header-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .invoice-mapping {
    min-width: 100%;
  }

  .item-count {
    text-align: center;
  }

  .item-row {
    flex-direction: column;
    padding: 12px;
    gap: 10px;
  }

  .item-info {
    width: 100%;
  }

  .combined-item {
    flex-wrap: wrap;
  }

  .item-mapping {
    flex: 1;
    width: 100%;
    margin-left: 0;
  }

  .modal-footer {
    padding: 16px;
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-backdrop {
    padding: 0;
  }

  .modal-content {
    border-radius: 0;
    max-height: 100vh;
  }

  .modal-header {
    padding: 12px;
  }

  .modal-title {
    font-size: 16px;
  }

  .modal-body {
    padding: 12px;
  }

  .invoice-header {
    padding: 10px;
  }

  .invoice-title {
    font-size: 13px;
    gap: 8px;
  }

  .item-row {
    padding: 10px;
  }

  .item-value {
    font-size: 12px;
  }

  .item-type-badge {
    font-size: 9px;
    padding: 2px 6px;
  }
}
</style>
